// Language Switching Module
// Handles UI language switching and translation updates

import { updateBackgroundTranslations } from './background.js';


// Render language options in the language tab
export function renderLanguageOptions() {
    const languageGrid = document.getElementById('languageGrid');
    if (!languageGrid || !window.i18n) return;

    const languages = window.i18n.getAvailableLanguages();
    const currentLang = window.i18n.getCurrentLanguage();

    languageGrid.innerHTML = languages.map(lang => `
        <div class="language-option ${lang.code === currentLang ? 'active' : ''}" 
             data-lang="${lang.code}" 
             onclick="switchLanguage('${lang.code}')">
            <div class="language-flag">${lang.flag}</div>
            <div class="language-name">${lang.name}</div>
        </div>
    `).join('');
}

// Switch to a different language
export function switchLanguage(langCode) {
    if (!window.i18n) return;

    if (window.i18n.setLanguage(langCode)) {
        // Re-render language options to update active state
        renderLanguageOptions();

        // Update all UI text
        updateUITranslations();

        // Synchronize with backend: send updated config
        if (typeof window.sendUIConfig === 'function') {
            window.sendUIConfig();
        }
    }

}

// Update all UI translations after language change
export function updateUITranslations() {
    if (!window.i18n) return;

    // Update settings modal title
    const modalTitle = document.querySelector('.modal-header h2');
    if (modalTitle) {
        modalTitle.textContent = '⚙️ ' + window.i18n.t('settings.title');
    }

    // Update tab labels
    const tabs = {
        'system-prompt': window.i18n.t('settings.tabs.systemPrompt'),
        'llm-profiles': window.i18n.t('settings.tabs.llmProfiles'),
        'tts-profiles': window.i18n.t('settings.tabs.ttsProfiles'),
        'language': window.i18n.t('settings.tabs.language')
    };

    Object.entries(tabs).forEach(([tabId, label]) => {
        const tab = document.querySelector(`[data-tab="${tabId}"]`);
        if (tab) tab.textContent = label;
    });

    // Update common buttons
    const cancelBtn = document.getElementById('cancelBtn');
    const saveBtn = document.getElementById('saveBtn');
    if (cancelBtn) cancelBtn.textContent = window.i18n.t('common.cancel');
    if (saveBtn) saveBtn.textContent = window.i18n.t('common.save');

    // Update system prompt tab
    const systemPromptLabel = document.querySelector('label[for="systemPrompt"]');
    const systemPrompt = document.getElementById('systemPrompt');
    if (systemPromptLabel) systemPromptLabel.textContent = window.i18n.t('settings.systemPrompt.label');
    if (systemPrompt) systemPrompt.placeholder = window.i18n.t('settings.systemPrompt.placeholder');

    // Update LLM profiles tab
    updateLLMProfilesTranslations();

    // Update TTS profiles tab
    updateTTSProfilesTranslations();

    // Update language tab
    const languageDesc = document.querySelector('.language-description');
    if (languageDesc) {
        languageDesc.textContent = window.i18n.t('settings.language.description');
    }


    // Update main UI
    const label = document.getElementById('label');
    const btn = document.getElementById('btn');

    if (label) label.textContent = window.i18n.t('chat.ready');
    if (btn) btn.textContent = window.i18n.t('chat.start');
    // Note: We do NOT reset the output/chat history when changing language
    // Only the interface language is changed, chat content is preserved


    // Re-fetch and re-render profiles to update button labels
    const activeTab = document.querySelector('.settings-tab.active');
    if (activeTab) {
        const tabId = activeTab.dataset.tab;
        if (tabId === 'llm-profiles' && window.fetchProfiles) {
            window.fetchProfiles();
        } else if (tabId === 'tts-profiles' && window.fetchTTSProfiles) {
            window.fetchTTSProfiles();
        }
    }

    // Update background picker labels
    updateBackgroundTranslations();

    // Update all data-i18n elements (catch-all for dynamic elements)
    if (window.i18n && window.i18n.translateElements) {
        window.i18n.translateElements();
    }
}

// Update LLM profiles section translations
function updateLLMProfilesTranslations() {
    if (!window.i18n) return;

    const addProfileBtn = document.getElementById('addProfileBtn');
    const profileFormTitle = document.getElementById('profileFormTitle');
    const cancelProfileBtn = document.getElementById('cancelProfileBtn');
    const saveProfileBtn = document.getElementById('saveProfileBtn');

    if (addProfileBtn) addProfileBtn.textContent = window.i18n.t('settings.llmProfiles.addButton');
    if (profileFormTitle) profileFormTitle.textContent = window.i18n.t('settings.llmProfiles.newConnection');
    if (cancelProfileBtn) cancelProfileBtn.textContent = window.i18n.t('common.cancel');
    if (saveProfileBtn) saveProfileBtn.textContent = window.i18n.t('common.save');

    // Update form labels
    const labels = {
        'profileName': window.i18n.t('settings.llmProfiles.nameLabel'),
        'profileApiUrl': window.i18n.t('settings.llmProfiles.apiUrlLabel'),
        'profileApiKey': window.i18n.t('settings.llmProfiles.apiKeyLabel'),
        'profileModelName': window.i18n.t('settings.llmProfiles.modelLabel')
    };

    Object.entries(labels).forEach(([fieldId, labelText]) => {
        const label = document.querySelector(`label[for="${fieldId}"]`);
        if (label) label.textContent = labelText;
    });

    // Update placeholders
    const placeholders = {
        'profileName': window.i18n.t('settings.llmProfiles.namePlaceholder'),
        'profileApiUrl': window.i18n.t('settings.llmProfiles.apiUrlPlaceholder'),
        'profileApiKey': window.i18n.t('settings.llmProfiles.apiKeyPlaceholder'),
        'profileModelName': window.i18n.t('settings.llmProfiles.modelPlaceholder')
    };

    Object.entries(placeholders).forEach(([fieldId, placeholderText]) => {
        const field = document.getElementById(fieldId);
        if (field) field.placeholder = placeholderText;
    });
}

// Update TTS profiles section translations
function updateTTSProfilesTranslations() {
    if (!window.i18n) return;

    const addTtsProfileBtn = document.getElementById('addTtsProfileBtn');
    const ttsProfileFormTitle = document.getElementById('ttsProfileFormTitle');
    const cancelTtsProfileBtn = document.getElementById('cancelTtsProfileBtn');
    const saveTtsProfileBtn = document.getElementById('saveTtsProfileBtn');

    if (addTtsProfileBtn) addTtsProfileBtn.textContent = window.i18n.t('settings.ttsProfiles.addButton');
    if (ttsProfileFormTitle) ttsProfileFormTitle.textContent = window.i18n.t('settings.ttsProfiles.newVoice');
    if (cancelTtsProfileBtn) cancelTtsProfileBtn.textContent = window.i18n.t('common.cancel');
    if (saveTtsProfileBtn) saveTtsProfileBtn.textContent = window.i18n.t('common.save');

    // Update form labels
    const labels = {
        'ttsProfileName': window.i18n.t('settings.ttsProfiles.nameLabel'),
        'ttsProviderType': window.i18n.t('settings.ttsProfiles.providerLabel'),
        'ttsApiUrl': window.i18n.t('settings.ttsProfiles.apiUrlLabel'),
        'ttsApiKey': window.i18n.t('settings.ttsProfiles.apiKeyLabel'),
        'ttsModelId': window.i18n.t('settings.ttsProfiles.modelIdLabel'),
        'ttsVoiceId': window.i18n.t('settings.ttsProfiles.voiceIdLabel')
    };

    Object.entries(labels).forEach(([fieldId, labelText]) => {
        const label = document.querySelector(`label[for="${fieldId}"]`);
        if (label) label.textContent = labelText;
    });

    // Update provider dropdown options
    const providerOptions = {
        'piper': window.i18n.t('settings.ttsProfiles.providerPiper'),
        'openai': window.i18n.t('settings.ttsProfiles.providerOpenAI'),
        'elevenlabs': window.i18n.t('settings.ttsProfiles.providerElevenLabs'),
        'azure': window.i18n.t('settings.ttsProfiles.providerAzure')
    };

    Object.entries(providerOptions).forEach(([value, optionText]) => {
        const option = document.querySelector(`#ttsProviderType option[value="${value}"]`);
        if (option) option.textContent = optionText;
    });

    // Update placeholders
    const placeholders = {
        'ttsProfileName': window.i18n.t('settings.ttsProfiles.namePlaceholder'),
        'ttsApiUrl': window.i18n.t('settings.ttsProfiles.apiUrlPlaceholder'),
        'ttsApiKey': window.i18n.t('settings.ttsProfiles.apiKeyPlaceholder'),
        'ttsModelId': window.i18n.t('settings.ttsProfiles.modelIdPlaceholder'),
        'ttsVoiceId': window.i18n.t('settings.ttsProfiles.voiceIdPlaceholder')
    };

    Object.entries(placeholders).forEach(([fieldId, placeholderText]) => {
        const field = document.getElementById(fieldId);
        if (field) field.placeholder = placeholderText;
    });
}
